import { type FormattedEvent } from '@eventbrite/consumer-search.event-renderer';
import { type RelatedEvent } from '../components/RelatedEvents';
import { type PromotedEvent } from '../components/RelatedEvents/types';

/**
 * Transforms a promoted event to a related event format
 *
 * @param promotedEvent - The promoted event to transform
 * @returns A related event object
 */
export function transformPromotedEventToRelatedEvent(promotedEvent: PromotedEvent): RelatedEvent {
  return {
    id: promotedEvent.id,
    name: promotedEvent.name,
    url: promotedEvent.url,
    imageUrl: promotedEvent.image?.url,
    startDate: promotedEvent.start_date ?? undefined,
    startTime: promotedEvent.start_time ?? undefined,
    timezone: promotedEvent.timezone ?? undefined,
    isFree: promotedEvent.ticket_availability?.is_free || false,
    isOnlineEvent: promotedEvent.is_online_event,
    isPromoted: true,
    formattedPriceString: promotedEvent.ticket_availability?.minimum_ticket_price?.display,
    venue: {
      id: promotedEvent.primary_venue?.id ?? '',
      name: promotedEvent.primary_venue?.name ?? '',
      address: promotedEvent.primary_venue?.address?.address_1 ?? '',
      city: promotedEvent.primary_venue?.address?.city ?? '',
      region: promotedEvent.primary_venue?.address?.region ?? '',
      country: promotedEvent.primary_venue?.address?.country ?? '',
    },
    urgencySignals: {
      new: promotedEvent.urgency_signals?.messages?.includes('new') || false,
      salesEndSoon: promotedEvent.urgency_signals?.messages?.includes('salesEndSoon') || false,
      fewTickets: promotedEvent.urgency_signals?.messages?.includes('fewTickets') || false,
      popular: promotedEvent.urgency_signals?.messages?.includes('popular') || false,
      goingFast: promotedEvent.urgency_signals?.messages?.includes('goingFast') || false,
    },
    specialDiscounts: {
      hasBogoTickets: promotedEvent.ticket_availability.has_bogo_tickets ?? false,
    },
    promotedListingMetadata: {
      adId: promotedEvent.promoted_listing_metadata?.ad_id ?? '',
      indexInSearchResults: promotedEvent.promoted_listing_metadata?.index_in_results ?? 0,
      isPromotedEvent: promotedEvent.promoted_listing_metadata?.is_promoted_event ?? false,
      placementId: promotedEvent.promoted_listing_metadata?.placement_id ?? '',
    },
    savedByYou: promotedEvent.saves?.saved_by_you ?? false,
    hasOpenDiscount:
      (promotedEvent.ticket_availability.open_discount &&
        promotedEvent.ticket_availability.open_discount.quantity_available > 0) ??
      false,
  };
}

/**
 * Transforms an array of promoted events to related events
 *
 * @param promotedEvents - Array of promoted events to transform
 * @returns Array of related events
 */
export function transformPromotedEventsToRelatedEvents(
  promotedEvents: PromotedEvent[] | null | undefined,
): RelatedEvent[] {
  if (!promotedEvents || !Array.isArray(promotedEvents)) {
    return [];
  }

  return promotedEvents.map(transformPromotedEventToRelatedEvent);
}

/**
 * Transforms a related event to a tracking event *
 * @param relatedEvent - The related event to transform
 * @returns A tracking event object
 */
export function transformRelatedEventToTrackingEvent(relatedEvent: RelatedEvent): FormattedEvent {
  return {
    id: relatedEvent.id ?? '',
    imageUrl: relatedEvent.imageUrl ?? '',
    isFree: relatedEvent.isFree ?? false,
    isPromoted: relatedEvent.isPromoted ?? false,
    promotedListingMetadata: relatedEvent.promotedListingMetadata,
    isOnlineEvent: relatedEvent.isOnlineEvent ?? false,
    locationInfo: '', // Deprecated, but defined as required
    name: relatedEvent.name ?? '',
    savedByYou: relatedEvent.savedByYou ?? false,
    startTime: relatedEvent.startTime ?? '',
    timezone: relatedEvent.timezone ?? '',
    url: relatedEvent.url ?? '',
  };
}
